/* ============================================================
   hh-collage.js — Love-notes collage + Instagram strip

   FILLED IN BY: T9 (Love notes collage). T10 will add the `instagram` mode.
   Keyed to [data-hh-collage]; the `data-collage-mode` attribute selects the
   behavior. Per-instance state lives on a WeakMap so multiple instances are
   safe; re-inits on shopify:section:load and tears down on :unload.

   SHARED HELPERS (so T10 can reuse them):
     • copyWidth(track, itemCount) — left edge of the second copy = one
       copy-width, used to wrap seamlessly (the track is rendered TWICE).
     • attachDrag(scrollEl, getCopyWidth, drag) — pointer grab/scrub on
       scrollLeft, wrapping at copy-width; returns a detach() cleanup fn.
   The loveNotes mode wires these + a glide/pause rAF autoplay loop.
   ============================================================ */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-hh-collage]';
  var instances = new WeakMap();

  var prefersReduce = !!(window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  function isMobile() { return window.innerWidth < 768; }

  /* ---- shared: one copy-width (left edge of the duplicated second copy) ---- */
  function copyWidth(track, itemCount) {
    if (!track || !track.children[itemCount]) return 0;
    return track.children[itemCount].offsetLeft;
  }

  /* ---- shared: pointer-drag scrubbing scrollLeft, wrapping at copy-width ----
     drag is a plain object { active, startX, startScroll } the caller owns so
     the autoplay loop can read drag.active to pause. Returns a detach fn. */
  function attachDrag(scrollEl, getCopyWidth, drag) {
    function onDown(e) {
      drag.active = true;
      drag.moved = false; // set true once the pointer moves past a small threshold
      drag.startX = e.clientX;
      drag.startScroll = scrollEl.scrollLeft;
      if (scrollEl.setPointerCapture) {
        try { scrollEl.setPointerCapture(e.pointerId); } catch (err) {}
      }
      scrollEl.style.cursor = 'grabbing';
    }
    function onMove(e) {
      if (!drag.active) return;
      if (Math.abs(e.clientX - drag.startX) > 4) drag.moved = true;
      scrollEl.scrollLeft = drag.startScroll - (e.clientX - drag.startX);
      var cw = getCopyWidth();
      if (cw > 0) {
        if (scrollEl.scrollLeft >= cw) { scrollEl.scrollLeft -= cw; drag.startScroll -= cw; }
        else if (scrollEl.scrollLeft < 0) { scrollEl.scrollLeft += cw; drag.startScroll += cw; }
      }
    }
    function onUp(e) {
      if (!drag.active) return;
      drag.active = false;
      if (scrollEl.releasePointerCapture && e && e.pointerId != null) {
        try { scrollEl.releasePointerCapture(e.pointerId); } catch (err) {}
      }
      scrollEl.style.cursor = 'grab';
    }
    scrollEl.addEventListener('pointerdown', onDown);
    scrollEl.addEventListener('pointermove', onMove);
    scrollEl.addEventListener('pointerup', onUp);
    scrollEl.addEventListener('pointercancel', onUp);
    scrollEl.addEventListener('pointerleave', onUp);
    return function detach() {
      scrollEl.removeEventListener('pointerdown', onDown);
      scrollEl.removeEventListener('pointermove', onMove);
      scrollEl.removeEventListener('pointerup', onUp);
      scrollEl.removeEventListener('pointercancel', onUp);
      scrollEl.removeEventListener('pointerleave', onUp);
    };
  }

  /* ---- love-notes mode ----------------------------------------------------- */
  function initLoveNotes(root) {
    var track = root.querySelector('.hh-love__track');
    if (!track) return null;

    var cards = track.querySelectorAll('.hh-love__card');
    if (cards.length === 0) return null;
    // The track is duplicated → the first copy is half the children.
    var itemCount = cards.length / 2;

    var drag = { active: false, startX: 0, startScroll: 0 };
    var getCopyWidth = function () { return copyWidth(track, itemCount); };
    var detachDrag = attachDrag(root, getCopyWidth, drag);

    // Apply the per-breakpoint top offset (mobile halves it — Home.jsx 200).
    function applyOffsets() {
      var mob = isMobile();
      for (var i = 0; i < cards.length; i++) {
        var top = parseInt(cards[i].getAttribute('data-top'), 10) || 0;
        cards[i].style.marginTop = (mob ? Math.round(top * 0.5) : top) + 'px';
      }
    }
    applyOffsets();

    var state = { raf: 0, cleanup: null };

    // resize → recompute offsets (gap is handled in CSS per breakpoint).
    var onResize = applyOffsets;
    window.addEventListener('resize', onResize);

    // Autoplay: glide a step, pause, glide, pause — paused while dragging.
    // Skipped entirely under reduced motion (drag still works).
    if (!prefersReduce) {
      var glide = parseInt(root.getAttribute('data-glide'), 10) || 2400; // ms
      var PAUSE = 1600;
      var STEP = isMobile() ? 200 : 260;
      var ease = function (t) { return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; };

      var phase = 'pause', phaseStart = performance.now(), from = 0, to = 0;

      var tick = function (now) {
        if (!drag.active) {
          if (phase === 'pause') {
            if (now - phaseStart >= PAUSE) {
              phase = 'glide'; phaseStart = now; from = root.scrollLeft; to = from + STEP;
            }
          } else {
            var t = Math.min(1, (now - phaseStart) / glide);
            root.scrollLeft = from + (to - from) * ease(t);
            if (t >= 1) { phase = 'pause'; phaseStart = now; }
          }
          var cw = getCopyWidth();
          if (cw > 0 && root.scrollLeft >= cw) { root.scrollLeft -= cw; from -= cw; to -= cw; }
        }
        state.raf = requestAnimationFrame(tick);
      };
      state.raf = requestAnimationFrame(tick);
    }

    state.cleanup = function () {
      window.removeEventListener('resize', onResize);
      detachDrag();
    };
    return state;
  }

  /* ---- instagram mode ------------------------------------------------------ */
  /* Port of InstagramStrip 243-279: a STATIC infinite drag loop (no rAF). The
     track is rendered TWICE; start mid-tile so tiles peek at both edges; drag
     scrubs + wraps; manual scroll wraps too; a tile click is suppressed if the
     pointer dragged (drag.moved). */
  function initInstagram(root) {
    var track = root.querySelector('.hh-ig__track');
    if (!track) return null;

    var tiles = track.querySelectorAll('.hh-ig__tile');
    if (tiles.length === 0) return null;
    // The track is duplicated → the first copy is half the children.
    var itemCount = tiles.length / 2;

    var drag = { active: false, moved: false, startX: 0, startScroll: 0 };
    var getCopyWidth = function () { return copyWidth(track, itemCount); };
    var detachDrag = attachDrag(root, getCopyWidth, drag);

    // Wrap on any manual scroll (the mockup wraps on scroll too — Home.jsx 310).
    function onScroll() {
      var cw = getCopyWidth();
      if (cw <= 0) return;
      if (root.scrollLeft >= cw) root.scrollLeft -= cw;
      else if (root.scrollLeft < 0) root.scrollLeft += cw;
    }
    root.addEventListener('scroll', onScroll);

    // Start mid-tile so tiles peek at both edges (Home.jsx 254). Read the real
    // tile width + gap from layout so it matches whatever CSS resolved to.
    function applyStartOffset() {
      var first = tiles[0];
      var second = track.children[1];
      var tileW = first ? first.offsetWidth : 0;
      // gap = left edge of 2nd tile − right edge of 1st tile.
      var gap = (second && first) ? (second.offsetLeft - (first.offsetLeft + tileW)) : 0;
      root.scrollLeft = (tileW + gap) * 0.5;
    }
    applyStartOffset();

    // Re-seat the start offset on resize (mobile/desktop tile sizes differ).
    var onResize = applyStartOffset;
    window.addEventListener('resize', onResize);

    // Suppress the tile's navigation if the pointer dragged.
    function onTileClick(e) {
      if (drag.moved) { e.preventDefault(); }
    }
    var tileClickEls = root.querySelectorAll('[data-hh-ig-tile]');
    for (var i = 0; i < tileClickEls.length; i++) {
      tileClickEls[i].addEventListener('click', onTileClick);
    }

    var state = { raf: 0, cleanup: null };
    state.cleanup = function () {
      root.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      for (var j = 0; j < tileClickEls.length; j++) {
        tileClickEls[j].removeEventListener('click', onTileClick);
      }
      detachDrag();
    };
    return state;
  }

  /* ---- lifecycle ----------------------------------------------------------- */
  function destroy(root) {
    var state = instances.get(root);
    if (!state) return;
    if (state.raf) cancelAnimationFrame(state.raf);
    if (state.cleanup) state.cleanup();
    instances.delete(root);
  }

  function init(sectionEl) {
    if (!sectionEl) return;
    var root = sectionEl.matches && sectionEl.matches(ROOT_SELECTOR)
      ? sectionEl
      : sectionEl.querySelector(ROOT_SELECTOR);
    if (!root) return; // no collage in this section — no-op

    destroy(root); // re-init cleanly (theme editor re-renders in place)

    var mode = root.getAttribute('data-collage-mode');
    var state = null;
    if (mode === 'loveNotes') {
      state = initLoveNotes(root);
    } else if (mode === 'instagram') {
      state = initInstagram(root);
    }
    if (state) instances.set(root, state);
  }

  function initAll(scope) {
    var ctx = scope || document;
    var roots = ctx.querySelectorAll(ROOT_SELECTOR);
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target && e.target.querySelector
      ? (e.target.matches && e.target.matches(ROOT_SELECTOR) ? e.target : e.target.querySelector(ROOT_SELECTOR))
      : null;
    if (root) destroy(root);
  });
})();
